/**
 * Footer slot toggle for Internal/External visibility before the first comment is posted.
 * Hidden once posted (header badge takes over).
 * Runs in its own ReactDOM.createRoot — no MUI/ThemeProvider. Styles: pdf-review.css
 */

import React, {useSyncExternalStore} from 'react';
import PropTypes from 'prop-types';

import {commentVisibilityStore} from '../store/commentVisibilityStore';
import {LockIcon, GlobeIcon} from './commentIcons';

const Lock = () => <LockIcon className="cet-icon" />;
const Globe = () => <GlobeIcon className="cet-icon" />;

const CommentEditorToggle = ({annotationId}) => {
  const {visibilityMap, postedAnnotationIds} = useSyncExternalStore(
    commentVisibilityStore.subscribe,
    commentVisibilityStore.getSnapshot
  );

  // Once the thread is posted, the header appears. Hide the footer toggle.
  if (postedAnnotationIds?.has(annotationId)) return null;

  const isInternal = visibilityMap.get(annotationId) ?? true;

  const handleSet = (newIsInternal) => {
    if (newIsInternal === isInternal) return;
    // Only update the store
    commentVisibilityStore.setVisibility(annotationId, newIsInternal);
  };

  return (
    <div className="cet-container">
      <div
        className="cet-toggle"
        role="group"
        aria-label="Comment thread visibility"
      >
        <button
          className={`cet-option${isInternal ? ' cet-option--active' : ''}`}
          onClick={() => handleSet(true)}
          aria-pressed={isInternal}
          data-tooltip="Internal comments will not be included in exports."
        >
          <Lock />
          Internal
        </button>
        <button
          className={`cet-option${!isInternal ? ' cet-option--active' : ''}`}
          onClick={() => handleSet(false)}
          aria-pressed={!isInternal}
          data-tooltip="External comments will be included in exports."
        >
          <Globe />
          External
        </button>
      </div>
    </div>
  );
};

CommentEditorToggle.propTypes = {
  annotationId: PropTypes.string.isRequired,
};

export default CommentEditorToggle;
